import React, { useState } from "react";
import { Button, Form, Input } from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  MailOutlined,
} from "@ant-design/icons";
import { propertyService } from "@iso/services";
import { ACCESS_REQUEST_STATUS } from "@iso/constants/onboarding";

const RESULT_COPY = {
  [ACCESS_REQUEST_STATUS.APPROVED]: {
    title: "You're in",
    icon: <CheckCircleOutlined />,
    body: (name) => (
      <>
        You already have access to <strong>{name}</strong>.
      </>
    ),
  },
  [ACCESS_REQUEST_STATUS.INVITED]: {
    title: "You've already been invited",
    icon: <MailOutlined />,
    body: (name) => (
      <>
        You've been invited to <strong>{name}</strong>. Open the invitation
        email to accept it.
      </>
    ),
  },
  [ACCESS_REQUEST_STATUS.PENDING]: {
    title: "Request sent",
    icon: <ClockCircleOutlined />,
    body: (name) => (
      <>
        We've asked the owner of <strong>{name}</strong> to approve your
        request. You'll be able to open it in AirAgri once they do.
      </>
    ),
  },
};

// "Joining an existing property": ask for read-only access by property code.
// An owner/admin always has to accept it.
const JoinPropertyStep = ({ onApproved, onFinish }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [result, setResult] = useState(null);

  const handleFinish = ({ publish_code }) => {
    const code = publish_code.trim();
    setErrMsg("");
    setLoading(true);
    propertyService
      .requestPropertyAccess(code)
      .then((res) => {
        setLoading(false);
        if (res && res.code === "0000") {
          setResult({ status: res.status, propertyName: res.property_name });
          if (res.status === ACCESS_REQUEST_STATUS.APPROVED) onApproved();
        } else {
          setErrMsg(
            (res && typeof res.message === "string" && res.message) ||
              "We couldn't send your request. Please try again."
          );
        }
      })
      .catch(() => {
        setLoading(false);
        setErrMsg("We couldn't send your request. Please check your connection and try again.");
      });
  };

  if (result) {
    const approved = result.status === ACCESS_REQUEST_STATUS.APPROVED;
    const copy = RESULT_COPY[result.status] || RESULT_COPY[ACCESS_REQUEST_STATUS.PENDING];
    return (
      <>
        <h1 className="ob-title" tabIndex={-1}>
          {copy.title}
        </h1>
        <div className="ob-status">
          <span className="ob-status-icon" aria-hidden="true">
            {copy.icon}
          </span>
          <p>{copy.body(result.propertyName || "the property")}</p>
        </div>
        <div className="ob-actions">
          <Button
            className="ob-btn-primary"
            onClick={() => onFinish(approved ? "/map" : null)}
          >
            {approved ? "Go to property" : "Finish setup"}
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      <h1 className="ob-title" tabIndex={-1}>
        Join your property
      </h1>
      <p className="ob-lead">
        Enter your property's code and we'll ask the owner to let you in.
        You'll start with view-only access, which they can change.
      </p>
      {errMsg && (
        <div className="ob-error" role="alert">
          {errMsg}
        </div>
      )}
      <Form name="onboarding_join" form={form} layout="vertical" onFinish={handleFinish}>
        <Form.Item
          name="publish_code"
          label="Property code"
          extra="The owner can find it on their property's QR sign, or in AirAgri under Properties."
          rules={[
            {
              required: true,
              whitespace: true,
              message: "Please enter the property code",
            },
          ]}
        >
          <Input placeholder="e.g. ABCD123" autoComplete="off" />
        </Form.Item>
        <div className="ob-actions">
          <Button className="ob-btn-primary" htmlType="submit" loading={loading}>
            Send request
          </Button>
          <button type="button" className="ob-link" onClick={() => onFinish(null)}>
            Skip for now
          </button>
        </div>
      </Form>
    </>
  );
};

export default JoinPropertyStep;
